import { Link, useNavigate } from "@tanstack/react-router";
import { Phone } from "lucide-react";
import { trackConversion } from "@/lib/gtag";
import logoImg from "@/assets/logo.png";

export function Logo() {
  return (
    <Link to="/" className="flex items-center gap-3">
      <img
        src={logoImg}
        alt="SE Premium Cabs — Melbourne taxi and airport transfer service"
        width={48}
        height={48}
        loading="eager"
        decoding="async"
        className="h-12 w-12 rounded-full object-cover"
      />
      <div className="leading-tight">
        <div className="text-sm font-bold tracking-widest text-foreground">
          SE PREMIUM CABS
        </div>
        <div className="text-[10px] tracking-[0.2em] text-primary">
          PREMIUM. RELIABLE. EVERY TIME.
        </div>
      </div>
    </Link>
  );
}

type Active = "home" | "airport" | "suburbs" | "chauffeur" | "corporate" | "contact" | null;

/**
 * "BOOK A RIDE" link. Always takes the user to the home page booking form.
 * On the home page it smooth-scrolls to #book (a plain hash link did nothing
 * once the URL hash was already #book); on any other page it navigates home
 * and then scrolls to the form.
 */
function BookRideLink({ className, children }: { className: string; children: React.ReactNode }) {
  const navigate = useNavigate();

  return (
    <a
      href="/#book"
      className={className}
      onClick={(e) => {
        e.preventDefault();
        const onHome = window.location.pathname === "/";
        const el = document.getElementById("book");
        if (onHome && el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          if (window.location.hash !== "#book") {
            window.history.replaceState(null, "", "#book");
          }
          return;
        }
        navigate({ to: "/", hash: "book" }).then(() => {
          requestAnimationFrame(() => {
            document.getElementById("book")?.scrollIntoView({ behavior: "smooth", block: "start" });
          });
        });
      }}
    >
      {children}
    </a>
  );
}

export function SiteHeader({ active = null }: { active?: Active }) {
  const cls = (key: Active) =>
    key === active
      ? "text-xs font-semibold tracking-widest text-primary border-b-2 border-primary pb-1"
      : "text-xs font-semibold tracking-widest text-foreground hover:text-primary";

  return (
    <header className="border-b border-border/60 bg-background">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5">
        <Logo />
        <nav className="hidden items-center gap-8 lg:flex">
          <Link to="/" className={cls("home")}>
            HOME
          </Link>
          <BookRideLink className={cls(null)}>BOOK A RIDE</BookRideLink>
          <Link to="/melbourne-airport-taxi" className={cls("airport")}>
            AIRPORT
          </Link>
          <Link to="/suburbs" className={cls("suburbs")}>
            SUBURBS
          </Link>
          <Link to="/chauffeur-services" className={cls("chauffeur")}>
            CHAUFFEUR
          </Link>
          <Link to="/corporate-taxi" className={cls("corporate")}>
            CORPORATE
          </Link>
          <Link to="/contact" className={cls("contact")}>
            CONTACT
          </Link>
        </nav>
        <a
          href="tel:0493100265"
          onClick={() => trackConversion("phoneCall")}
          className="flex items-center gap-2 rounded-full border border-primary px-5 py-2.5 text-xs font-semibold tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          <Phone className="h-4 w-4" />
          0493 100 265
        </a>
      </div>
      <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-border/60 px-6 py-3 text-[10px] font-semibold tracking-widest lg:hidden">
        <Link
          to="/"
          className={active === "home" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          HOME
        </Link>
        <BookRideLink className="text-muted-foreground hover:text-primary">BOOK</BookRideLink>
        <Link
          to="/melbourne-airport-taxi"
          className={active === "airport" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          AIRPORT
        </Link>
        <Link
          to="/suburbs"
          className={active === "suburbs" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          SUBURBS
        </Link>
        <Link
          to="/chauffeur-services"
          className={active === "chauffeur" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          CHAUFFEUR
        </Link>
        <Link
          to="/corporate-taxi"
          className={active === "corporate" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          CORPORATE
        </Link>
        <Link
          to="/contact"
          className={active === "contact" ? "text-primary" : "text-muted-foreground hover:text-primary"}
        >
          CONTACT
        </Link>
      </nav>
    </header>
  );
}